/**
 * Engine-side lookup from object id to live Rapier body, so the carry controller can
 * grab the body of whatever the store says is carried. A module map rather than React
 * context: it is consulted inside `useFrame` and must not cause renders.
 */

import type { RapierRigidBody } from '@react-three/rapier';
import { forgetObjectHome, recordObjectHome } from './objectHomes';

/**
 * Collision groups for carryable objects (Rapier packs membership in the high 16
 * bits, filter in the low 16). Carryables live in group 1. While carried, a box's
 * filter drops group 1: it keeps colliding with the world but ignores its fellow
 * boxes — so grabbing the bottom of a stack lets the top one settle down gently
 * instead of being batted away by an infinite-mass kinematic sweep.
 */
const CARRYABLE_MEMBERSHIP = 0x0002;
export const CARRYABLE_GROUPS = (CARRYABLE_MEMBERSHIP << 16) | 0xffff;
export const CARRIED_GROUPS = (CARRYABLE_MEMBERSHIP << 16) | (0xffff & ~CARRYABLE_MEMBERSHIP);

/**
 * Collision groups for a robot dog while it is walking (design D4). It keeps the
 * carryable membership above — it is still, in principle, a carryable — but drops its
 * filter to nothing: a walking companion is passable, not solid, because a 0.34 m dog is
 * a plug on a 0.6 m hedge crest and a trap on a ladder the player cannot reason about.
 *
 * A collider that collides with nothing still gets picked up: `useInteractionTarget`'s
 * ray group-filters nothing at all and selects by a `userData` predicate instead, so
 * "Pick up" still appears. That predicate is also why this cannot be a sensor collider —
 * `useInteractionTarget` filters sensors out by name, which would make the dog untargetable.
 */
export const COMPANION_GROUPS = (CARRYABLE_MEMBERSHIP << 16) | 0x0000;

const bodies = new Map<string, RapierRigidBody>();

export function registerObjectBody(id: string, body: RapierRigidBody): void {
  bodies.set(id, body);
  // Where it was placed, recorded the moment it exists, so an object that is never picked
  // up still has somewhere to come back to if it leaves the world (design D7).
  recordObjectHome(id, body);
}

export function unregisterObjectBody(id: string): void {
  bodies.delete(id);
  forgetObjectHome(id);
}

export function getObjectBody(id: string): RapierRigidBody | undefined {
  return bodies.get(id);
}

/** Half a carryable's size, straight off its collider — no allocation, read-only. */
export function cuboidHalfExtents(body: RapierRigidBody): { x: number; y: number; z: number } {
  const shape = body.collider(0).shape as { halfExtents?: { x: number; y: number; z: number } };
  return shape.halfExtents ?? DEFAULT_HALF_EXTENTS;
}

const DEFAULT_HALF_EXTENTS = { x: 0.225, y: 0.225, z: 0.225 };

/** Visit every registered carryable — used to find what rests on a grabbed box. */
export function forEachObjectBody(visit: (id: string, body: RapierRigidBody) => void): void {
  for (const [id, body] of bodies) visit(id, body);
}
